import React, { useState, useEffect, useCallback, useRef } from 'react';
import { useLocation } from 'react-router-dom';
import { Helmet } from 'react-helmet-async';
import { IonButton } from '@ionic/react';
import { useToast } from 'contexts/ToastContext';
import billingService from 'services/billing';
import Addons from 'services/addons';
import Credits, { CREDIT_CHANGED_EVENT, formatBalance } from 'services/credits';
import AddCreditsModal from 'components/billing/AddCreditsModal';
import CouponModal from 'components/billing/CouponModal';
import AddonCard, { CopyButton } from 'components/billing/AddonCard';
import Pagination, { usePagination } from 'components/atoms/Pagination';
import DataTable from '../../components/shared/DataTable';
import LedgerDatePicker from './LedgerDatePicker';
import './BillingPage.css';

// Recent-activity table page size — matches the Usage page so both tables
// paginate identically.
const LEDGER_PAGE_SIZE = 25;

// Invoices page size — loaded live from Stripe, cursor-paginated. Smaller
// than the ledger because each row is a real payment (there are far fewer
// of them) and Stripe's list call is a network round-trip per page.
const INVOICES_PAGE_SIZE = 10;

// Heuristic shown under the big balance number.  We picked the Light
// Managed App tier ($0.05/hr) as the "if always on" example — it's the
// cheapest tier so the runtime estimate stays optimistic and is honest
// about being a lower bound. Future work: derive this from the user's
// own currently-running things.
const HEURISTIC_BURN_USD_PER_HOUR = 0.05;

// A day of runway at the heuristic burn rate, expressed in milli-cents
// ($1 = 100_000 milli-cents).  Used to drive the "Add credits to
// enable" inline warning on addon cards — if the user's balance is
// below a single day of even the cheapest baseline, enabling a paid
// addon is almost certain to 402 within hours.
const ONE_DAY_MILLI_CENTS = HEURISTIC_BURN_USD_PER_HOUR * 24 * 100000;

const BillingPage = () => {
  const [billingData, setBillingData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [addCreditsOpen, setAddCreditsOpen] = useState(false);
  const [couponOpen, setCouponOpen] = useState(false);
  const [addonBusy, setAddonBusy] = useState({ egress: false });
  // Activity-table data. `ledgerRows` is the set currently loaded into the
  // numbered pager; older server pages are pulled in transparently when the
  // user pages past what's loaded (cursor in `ledgerCursor`, null ⇒ no more).
  // `ledgerJumpDate` anchors the table at a chosen day; empty ⇒ latest. The
  // table itself paginates client-side via the shared <Pagination> atom,
  // exactly like the Usage page's "Recent executions" table.
  const [ledgerRows, setLedgerRows] = useState([]);
  const [ledgerCursor, setLedgerCursor] = useState(null);
  const [ledgerLoadingMore, setLedgerLoadingMore] = useState(false);
  const [ledgerJumpDate, setLedgerJumpDate] = useState('');
  // Bumped whenever the table's row set is reseeded (fresh snapshot / date
  // jump) so the pager resets to page 1.
  const [ledgerAnchor, setLedgerAnchor] = useState(0);
  const [ledgerJumping, setLedgerJumping] = useState(false);
  // Freeze the table area's height during a jump so swapping rows for the
  // loading skeleton doesn't collapse the card and snap it back.
  const [frozenLedgerHeight, setFrozenLedgerHeight] = useState(null);
  const ledgerBodyRef = useRef(null);
  const ledgerRef = useRef(null);
  // ── Invoices (Stripe receipts) ──────────────────────────────────────
  // Loaded live from Stripe via /credits/invoices, cursor-paginated. We
  // accumulate rows across pages and append on "Load more". `invoicesCursor`
  // null ⇒ no more pages (we've hit the oldest invoice).
  const [invoices, setInvoices] = useState([]);
  const [invoicesCursor, setInvoicesCursor] = useState(null);
  const [invoicesLoading, setInvoicesLoading] = useState(true);
  const [invoicesLoadingMore, setInvoicesLoadingMore] = useState(false);
  const [invoicesLoaded, setInvoicesLoaded] = useState(false);
  // Mirror of billingData kept in a ref so post-checkout poll intervals
  // can read fresh state without re-rendering the effect closure.
  const billingDataRef = useRef(null);
  const { showToast } = useToast();
  const location = useLocation();

  const loadBillingData = useCallback(async ({ silent = false } = {}) => {
    try {
      if (!silent) setLoading(true);
      // bucket='hour' aggregates per-minute Managed App deducts into
      // per-hour rows. Without this, a continuously-running app produces
      // 60 near-identical "managed_app_minute  −$0.0008" rows per hour
      // and the activity table becomes unreadable. Topups, grants, and
      // coupons still render as individual rows.
      const data = await billingService.getBillingInfo({ bucket: 'hour' });
      // Only push to React state when something actually changed —
      // prevents the post-checkout poll loop from re-rendering the
      // same numbers every 1.5s (visible "flash" while waiting on the
      // webhook).
      const prev = billingDataRef.current;
      const changed = !prev
        || prev?.credits?.balanceMilliCents !== data?.credits?.balanceMilliCents
        || (prev?.recentLedger?.length || 0) !== (data?.recentLedger?.length || 0);
      billingDataRef.current = data;
      if (changed) setBillingData(data);
    } catch (error) {
      console.error('Error loading billing data:', error);
    } finally {
      setLoading(false);
    }
  }, []);

  // Mount effect: load data + handle Stripe-checkout return.
  // The credits success URL pattern is `?checkout=success&session_id=X`
  // (set in AddCreditsModal). The Stripe webhook arrives ASYNC after
  // the redirect — sometimes a fraction of a second, sometimes 5+ s
  // depending on stripe-cli forward latency / backend cold-start. A
  // single setTimeout was almost always firing before addCredits
  // actually ran, leaving the page stuck at the old balance.
  //
  // Instead: snapshot the current balance, then poll /billing/info
  // every 1.5s for up to 20s, stopping as soon as the balance changes
  // (the webhook posted) or we hit the cap (something's wrong — leave
  // it for the user to refresh).
  useEffect(() => {
    const urlParams = new URLSearchParams(window.location.search);
    const checkoutParam = urlParams.get('checkout');

    if (checkoutParam === 'success') {
      try { showToast('Credits added — thanks!', 'success'); } catch {}
      window.history.replaceState({}, document.title, location.pathname);
      // No polling. The webhook is the source of truth — when it fires,
      // CREDIT_CHANGED_EVENT triggers a refetch via the other useEffect
      // below. User can also just refresh the page if needed.
      loadBillingData();
      return;
    }
    loadBillingData();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Refetch whenever a credit-affecting event fires (top-up returns,
  // addon enable/disable on another tab). Same hook the header chip
  // uses — keeps everything in sync without polling.
  //
  // Also refetch on window focus + tab-visible. Enforcement reads the LIVE
  // balance off the account row, but a credit-affecting action taken
  // elsewhere (a workflow run via CLI, a deduct in another tab) does NOT
  // fire CREDIT_CHANGED_EVENT in THIS tab — so without a focus refetch the
  // card can show a stale, higher balance while the user is actually
  // blocked. Refetching when the tab regains focus closes that gap.
  useEffect(() => {
    const handler = () => loadBillingData({ silent: true });
    const onFocus = () => loadBillingData({ silent: true });
    const onVisibility = () => {
      if (document.visibilityState === 'visible') loadBillingData({ silent: true });
    };
    window.addEventListener(CREDIT_CHANGED_EVENT, handler);
    window.addEventListener('focus', onFocus);
    document.addEventListener('visibilitychange', onVisibility);
    return () => {
      window.removeEventListener(CREDIT_CHANGED_EVENT, handler);
      window.removeEventListener('focus', onFocus);
      document.removeEventListener('visibilitychange', onVisibility);
    };
  }, [loadBillingData]);

  // Short poll while the Billing page is open + visible, so the balance
  // card never drifts far from the live (enforcement) value even if the
  // user leaves the tab open after running things elsewhere. Paused while
  // the tab is hidden to avoid needless background fetches.
  useEffect(() => {
    const POLL_MS = 30000;
    const tick = () => {
      if (document.visibilityState === 'visible') loadBillingData({ silent: true });
    };
    const interval = setInterval(tick, POLL_MS);
    return () => clearInterval(interval);
  }, [loadBillingData]);

  // Reseed the activity table to page 1 (latest) whenever a fresh billing
  // snapshot lands. "Load more" and "jump to date" mutate ledgerRows
  // directly (leaving billingData untouched), so this only fires on real
  // reloads (mount / post-checkout / credit-changed) — and there we also
  // clear any active jump so the table returns to latest.
  useEffect(() => {
    if (!billingData) return;
    setLedgerRows(Array.isArray(billingData.recentLedger) ? billingData.recentLedger : []);
    setLedgerCursor(billingData.recentLedgerCursor || null);
    setLedgerJumpDate('');
    setLedgerAnchor((n) => n + 1);
  }, [billingData]);

  // Pull the next (older) server page and append it to the loaded set. The
  // numbered pager calls this transparently when the user pages past what's
  // already loaded while older history still exists on the server.
  const fetchOlderLedgerPage = useCallback(async () => {
    if (!ledgerCursor || ledgerLoadingMore) return;
    setLedgerLoadingMore(true);
    try {
      const data = await billingService.getBillingInfo({ bucket: 'hour', cursor: ledgerCursor });
      // Append; ids are unique across pages (hour buckets are hour-aligned,
      // minute rows are distinct sks), so no dedupe needed.
      setLedgerRows((prev) => [...prev, ...(Array.isArray(data.recentLedger) ? data.recentLedger : [])]);
      setLedgerCursor(data.recentLedgerCursor || null);
    } catch (error) {
      console.error('Error loading more activity:', error);
    } finally {
      setLedgerLoadingMore(false);
    }
  }, [ledgerCursor, ledgerLoadingMore]);

  // ── Invoices: first page on mount, "Load more" appends older pages ──
  // The backend fails soft (always 200, empty list on a Stripe hiccup / an
  // account that's never paid), so we don't need to surface an error state —
  // an empty list just renders the "No invoices yet" empty state.
  const loadFirstInvoicesPage = useCallback(async () => {
    setInvoicesLoading(true);
    try {
      const data = await Credits.getInvoices({ limit: INVOICES_PAGE_SIZE });
      setInvoices(Array.isArray(data?.invoices) ? data.invoices : []);
      setInvoicesCursor(data?.hasMore ? (data?.nextCursor || null) : null);
    } catch (error) {
      console.error('Error loading invoices:', error);
      setInvoices([]);
      setInvoicesCursor(null);
    } finally {
      setInvoicesLoading(false);
      setInvoicesLoaded(true);
    }
  }, []);

  const loadMoreInvoices = useCallback(async () => {
    if (!invoicesCursor || invoicesLoadingMore) return;
    setInvoicesLoadingMore(true);
    try {
      const data = await Credits.getInvoices({ limit: INVOICES_PAGE_SIZE, cursor: invoicesCursor });
      setInvoices((prev) => [...prev, ...(Array.isArray(data?.invoices) ? data.invoices : [])]);
      setInvoicesCursor(data?.hasMore ? (data?.nextCursor || null) : null);
    } catch (error) {
      console.error('Error loading more invoices:', error);
    } finally {
      setInvoicesLoadingMore(false);
    }
  }, [invoicesCursor, invoicesLoadingMore]);

  // Load the first invoices page on mount. Also refetch when a credit-
  // affecting event fires (a top-up just completed → a new invoice exists),
  // reusing the same global event the balance/ledger listen to.
  useEffect(() => {
    loadFirstInvoicesPage();
    const handler = () => loadFirstInvoicesPage();
    window.addEventListener(CREDIT_CHANGED_EVENT, handler);
    return () => window.removeEventListener(CREDIT_CHANGED_EVENT, handler);
  }, [loadFirstInvoicesPage]);

  // Picking a day jumps the table straight to that day (and older). Empty
  // value (cleared field / "Back to latest") snaps back to latest from the
  // last snapshot — no refetch, it already holds the newest page.
  const onLedgerJumpDateChange = useCallback(async (dateStr) => {
    setLedgerJumpDate(dateStr);
    if (!dateStr) {
      const snap = billingDataRef.current;
      setLedgerRows(Array.isArray(snap?.recentLedger) ? snap.recentLedger : []);
      setLedgerCursor(snap?.recentLedgerCursor || null);
      setLedgerAnchor((n) => n + 1);
      return;
    }
    // Anchor at the START of the day AFTER the picked date (UTC, matching
    // the ledger's UTC hour buckets) so the whole picked day sits at the
    // top of the result, with older activity below it.
    const [y, m, d] = dateStr.split('-').map(Number);
    const before = new Date(Date.UTC(y, m - 1, d + 1)).toISOString();
    // Pin the current height so the skeleton doesn't collapse the card.
    setFrozenLedgerHeight(ledgerBodyRef.current?.offsetHeight || null);
    setLedgerJumping(true);
    try {
      const data = await billingService.getBillingInfo({ bucket: 'hour', before });
      setLedgerRows(Array.isArray(data.recentLedger) ? data.recentLedger : []);
      setLedgerCursor(data.recentLedgerCursor || null);
      setLedgerAnchor((n) => n + 1);
    } catch (error) {
      console.error('Error jumping to date:', error);
    } finally {
      setLedgerJumping(false);
      setFrozenLedgerHeight(null);
    }
  }, []);

  // Cap the date picker at today — no future ledger entries exist.
  const todayStr = new Date().toISOString().slice(0, 10);

  const credits = billingData?.credits || null;
  // Warm pooling moved to a per-workflow "Warm Agent" toggle in each
  // pipeline's settings tab — the legacy whole-workspace warm-pool addon
  // card was removed from /billing, so we only surface dedicatedEgress here.
  const addons = billingData?.addons || { dedicatedEgress: {} };

  // Numbered pagination over the loaded ledger rows — same UX + page size as
  // the Usage page's "Recent executions" table. Resets to page 1 whenever the
  // row set is reseeded (fresh snapshot or date jump → `ledgerAnchor` bumps).
  const {
    page: ledgerPage,
    setPage: setLedgerPage,
    pageCount: ledgerPageCount,
    total: ledgerTotal,
    pagedRows: recentLedger,
  } = usePagination(ledgerRows, LEDGER_PAGE_SIZE, [ledgerAnchor]);

  // The pager only knows about loaded rows; older history may still sit
  // behind a server cursor. If the user is on the last loaded page and a
  // cursor exists, "Next" fetches the next server page first, then advances.
  const ledgerHasMoreServer = !!ledgerCursor;
  const ledgerEffectivePageCount = ledgerHasMoreServer ? ledgerPageCount + 1 : ledgerPageCount;
  const onLedgerPageChange = useCallback((next) => {
    if (next > ledgerPageCount && ledgerHasMoreServer) {
      // Reaching beyond the loaded set — pull the next server page; the new
      // rows extend ledgerRows so the pager can advance onto them.
      fetchOlderLedgerPage().then(() => setLedgerPage(next));
      return;
    }
    setLedgerPage(next);
  }, [ledgerPageCount, ledgerHasMoreServer, fetchOlderLedgerPage, setLedgerPage]);

  const balanceMc = Number(credits?.balanceMilliCents) || 0;
  const balanceUsd = balanceMc / 100000;
  const insufficient = balanceMc < ONE_DAY_MILLI_CENTS;

  // ── Dedicated Egress action handlers ────────────────────────────────
  const onToggleEgress = async () => {
    const isEnabled = !!addons.dedicatedEgress?.enabled;
    setAddonBusy((s) => ({ ...s, egress: true }));
    try {
      if (isEnabled) {
        await Addons.disableDedicatedEgress();
        showToast('Dedicated egress disabled', 'success');
      } else {
        await Addons.enableDedicatedEgress();
        showToast('Dedicated egress enabled — provisioning IP…', 'success');
      }
      Credits.fireChangedEvent();
      await loadBillingData();
    } catch (err) {
      const msg = err?.body?.message || err?.message || 'Action failed';
      showToast(msg, 'error');
    } finally {
      setAddonBusy((s) => ({ ...s, egress: false }));
    }
  };

  // ── Card detail row helpers ─────────────────────────────────────────
  const egress = addons.dedicatedEgress || {};
  const egressPerDay = Number(egress.perDayUsd);
  const egressDetailRows = egress.enabled
    ? [
      ['IP address', egress.eipAddress ? (
        <span className="addon-ip">
          <code>{egress.eipAddress}</code>
          <CopyButton text={egress.eipAddress} />
        </span>
      ) : 'Provisioning…'],
    ]
    : [];

  return (
    <>
      <Helmet>
        <title>Billing - Zibby</title>
        <meta name="description" content="Manage your Zibby credit balance and add-ons." />
      </Helmet>
      <div className="billing-page page-fade-in main-body page-layout">
        <div className="page-header-center">
          <h1>Billing</h1>
          <p>Credit balance, add-ons, and payment history</p>
        </div>

        {/* ── A. BALANCE CARD ─────────────────────────────────────────── */}
        <div className="page-card balance-hero">
          <div className="balance-hero-body">
            <div className="balance-hero-label">Balance</div>
            <div className={`balance-hero-amount ${balanceMc < 0 ? 'is-paused' : balanceMc < 100000 ? 'is-low' : ''}`}>
              {loading ? '—' : formatBalance(balanceMc)}
            </div>
            <div className="balance-hero-runtime">
              {loading
                ? ' '
                : balanceUsd > 0
                  ? 'Burn rate depends on what you run — see rate card below.'
                  : 'Add credits to start running workflows and apps.'}
            </div>
          </div>
          <div className="balance-hero-actions">
            {/* Single CTA — Recent activity section below shows the ledger,
                so a "View history" button here is redundant. The shared
                .btn-white atom is an IonButton class — its --background
                etc. CSS vars don't apply to a plain <button> so we use
                IonButton here to match the project-settings + sentry
                page pattern. */}
            <IonButton
              fill="clear"
              size="small"
              className="btn-white"
              onClick={() => setAddCreditsOpen(true)}
            >
              Add credits
            </IonButton>
            {/* Promo-code redemption — secondary, doesn't compete with the
                primary CTA. Opens a sibling modal; success refreshes the
                balance via the global credit-changed event the modal fires. */}
            <button
              type="button"
              className="balance-hero-coupon-link"
              onClick={() => setCouponOpen(true)}
            >
              Use coupon
            </button>
          </div>
        </div>

        {/* ── B. ADDONS SECTION ──────────────────────────────────────── */}
        <div className="page-card addons-section">
          {/* Title + blurb live on the same row — blurb sits at top-right
              aligned with the "Add-ons" heading. No border between
              header and grid. */}
          <div className="addons-section-header">
            <h2>Add-ons</h2>
            <p className="addons-section-blurb">
              Optional infrastructure billed per-minute from your credit balance. Disable any time.
            </p>
          </div>

          <div className="addons-grid">
            <AddonCard
              name="Dedicated Static IP"
              description="Pin all outbound traffic to one static IP so you can allowlist Zibby on a firewalled GitLab / GitHub Enterprise / Salesforce."
              perDayUsd={Number.isFinite(egressPerDay) ? egressPerDay : 1.67}
              perMonthEstimateUsd={Number.isFinite(egressPerDay) ? egressPerDay * 30 : 50}
              enabled={!!egress.enabled}
              detailRows={egressDetailRows}
              insufficientBalance={insufficient}
              busy={addonBusy.egress}
              onEnable={onToggleEgress}
              onDisable={onToggleEgress}
              onAddCredits={() => setAddCreditsOpen(true)}
            />
            {/* Muted placeholder fills the right slot so the 2-up Add-ons
                row isn't lopsided after the legacy Warm Pool card was
                removed. No actions — purely visual balance. */}
            <AddonCard
              placeholder
              name="More add-ons coming soon"
              description="Additional infrastructure options will appear here."
            />
          </div>
        </div>

        {/* ── C. RATE CARD ───────────────────────────────────────────── */}
        {/* Both tables on this page render via the shared DataTable atom
            (components/shared/DataTable) — same component the Projects /
            Tokens / Pipelines pages use. No bespoke table styles. */}
        <div className="page-card">
          <div className="card-header">
            <h2>Rate card</h2>
          </div>
          <DataTable
            rowKey="label"
            columns={[
              { header: 'Resource', key: 'label',  render: (r) => r.label },
              { header: 'Billing',  key: 'basis',  render: (r) => r.basis },
              { header: 'Rate',     key: 'rate',   render: (r) => r.rate },
            ]}
            data={(() => {
              const rates = credits?.activeRates || {};
              const wf = rates.workflowRun || {};
              const ma = rates.managedApp || {};
              const copilot = rates.copilot || {};
              const perMin = (mc) => `$${((Number(mc) || 0) / 100000).toFixed(4)}/min`;
              const perHr  = (mc) => `$${((Number(mc) || 0) * 60 / 100000).toFixed(2)}/hr`;
              return [
                { label: 'Workflow run — cold (new Fargate task)',     basis: 'per minute', rate: wf.cold ? `${perMin(wf.cold)} (~ ${perHr(wf.cold)})` : '—' },
                { label: 'Workflow run — warm (reused task)',          basis: 'per minute', rate: wf.warm ? `${perMin(wf.warm)} (~ ${perHr(wf.warm)})` : '—' },
                { label: 'Managed App — Light tier',     basis: 'per minute', rate: ma.light    ? perHr(ma.light)    : '—' },
                { label: 'Managed App — Standard tier',  basis: 'per minute', rate: ma.standard ? perHr(ma.standard) : '—' },
                // Zibby Copilot — seat (chat) is a per-active-user-per-month
                // platform fee (a DIFFERENT unit from the per-minute rows). The
                // seat rate is stored in milli-cents (÷100,000 = USD). Actions
                // ("do = usage") bill on the normal workflow-run path above, so
                // there's no separate Copilot usage price — point at it instead.
                { label: 'Zibby Copilot — chat seat', basis: 'per active user / month', rate: copilot.seatPerUserMonthMilliCents ? `$${(Number(copilot.seatPerUserMonthMilliCents) / 100000).toFixed(2)}` : '—' },
                { label: 'Zibby Copilot — actions (usage)', basis: 'per agent execution', rate: '— (billed as workflow usage)' },
                // `heavy` is a DEPRECATED alias for `standard` in the SSOT
                // (pricing-rates.js) — no app is sized to it at runtime and
                // there's no UI to pick it. Don't render it as a co-equal
                // third tier; it just confuses the user into thinking there's
                // a pricier option they might land on.
              ];
            })()}
          />
        </div>

        {/* ── D. LEDGER (everything credit-affecting) ────────────────── */}
        <div className="page-card" ref={ledgerRef}>
          <div className="card-header">
            <h2>Recent activity</h2>
            {/* Navigate by date — the activity table is cursor-paginated
                (no random page jumps), so date is the natural jump axis for
                a ledger. Custom calendar: only a day-cell click jumps. */}
            <div className="ledger-jump">
              {ledgerJumpDate && (
                <button
                  type="button"
                  className="ledger-jump-reset"
                  onClick={() => onLedgerJumpDateChange('')}
                  disabled={ledgerJumping}
                >
                  Reset
                </button>
              )}
              <LedgerDatePicker
                value={ledgerJumpDate}
                max={todayStr}
                disabled={ledgerJumping}
                onSelect={onLedgerJumpDateChange}
              />
            </div>
          </div>
          <div
            className="ledger-body"
            ref={ledgerBodyRef}
            style={ledgerJumping && frozenLedgerHeight ? { minHeight: `${frozenLedgerHeight}px` } : undefined}
          >
          {ledgerJumping ? (
            // Height-preserving skeleton (3 rows is enough) — keeps the card
            // from collapsing/snapping while the jump loads.
            <div className="data-table-wrapper">
              <table className="data-table">
                <thead>
                  <tr><th>When</th><th>Reason</th><th>Source</th><th>Amount</th></tr>
                </thead>
                <tbody>
                  {[0, 1, 2].map((i) => (
                    <tr key={i} className="data-table-row data-table-skeleton-row">
                      <td><div className="skeleton-bar" style={{ width: '72%' }} /></td>
                      <td><div className="skeleton-bar" style={{ width: '54%' }} /></td>
                      <td><div className="skeleton-bar" style={{ width: '18%' }} /></td>
                      <td><div className="skeleton-bar" style={{ width: '38%' }} /></td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          ) : (
          <>
          <DataTable
            rowKey="id"
            loading={loading}
            columns={[
              { header: 'When',   key: 'when',   render: (r) => {
                if (!r.ts) return '—';
                const when = new Date(r.ts).toLocaleString();
                // For hour-bucketed rows, show "17:00 — 17:59" so the user
                // sees the hour window instead of a misleading single
                // timestamp that's actually a sum over 60 minutes.
                if (r.bucket === 'hour') {
                  const start = new Date(r.ts);
                  const end = new Date(start.getTime() + 3599999);
                  const fmt = (d) => d.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' });
                  const dateStr = start.toLocaleDateString();
                  return `${dateStr}, ${fmt(start)}–${fmt(end)}`;
                }
                return when;
              }},
              { header: 'Reason', key: 'reason', render: (r) => {
                const reason = r.reason || '—';
                // `× N` badge tells the user how many underlying rows were
                // folded into this bucket. Subtle styling so it reads as
                // metadata, not a primary identifier.
                if (r.count > 1) {
                  return (
                    <span>
                      {reason}
                      <span style={{ marginLeft: 8, color: 'rgba(255,255,255,0.45)', fontSize: 12, fontVariantNumeric: 'tabular-nums' }}>
                        × {r.count}
                      </span>
                    </span>
                  );
                }
                return reason;
              }},
              { header: 'Source', key: 'source', render: (r) => r.source || '—' },
              { header: 'Amount', key: 'amount', render: (r) => {
                const delta = Number(r.deltaMilliCents) || 0;
                const isCredit = delta > 0;
                // 'auto' precision keeps cent-level values clean ($0.05)
                // while showing sub-cent deductions at 4dp ($0.0008) so
                // per-minute Managed App charges don't all read as "$0.00".
                return (
                  <span style={{ color: isCredit ? '#4ade80' : '#f87171', fontVariantNumeric: 'tabular-nums' }}>
                    {isCredit ? '+' : ''}{formatBalance(delta, { precision: 'auto' })}
                  </span>
                );
              }},
            ]}
            data={recentLedger.map((r, i) => ({ ...r, id: r.id || `${r.ts || ''}-${i}` }))}
            emptyState={ledgerJumpDate ? {
              title: 'No activity on or before this date',
              description: 'Try a more recent date, or go back to latest.',
            } : {
              title: 'No activity yet',
              description: 'Top up credits or run workflows / apps to see entries here.',
            }}
          />
          {/* Numbered pagination — same control + page size as the Usage
              page. Older history sits behind a server cursor; when present we
              advertise one extra page and pull it transparently on "Next"
              (the backend hour-aligns pages so it never duplicates an hour). */}
          {!loading && (
            <Pagination
              page={ledgerPage}
              pageCount={ledgerEffectivePageCount}
              total={ledgerTotal}
              pageSize={LEDGER_PAGE_SIZE}
              onPageChange={onLedgerPageChange}
            />
          )}
          </>
          )}
          </div>
        </div>

        {/* ── E. INVOICES / RECEIPTS ─────────────────────────────────── */}
        {/* Loaded live from Stripe. Each top-up generates a real invoice
            (invoice_creation enabled on the Checkout session); this lists
            them newest-first with View (hosted receipt) + PDF links. Cursor-
            paginated via a "Load more" button. */}
        <div className="page-card">
          <div className="card-header">
            <h2>Invoices</h2>
          </div>
          <DataTable
            rowKey="id"
            loading={invoicesLoading && !invoicesLoaded}
            columns={[
              { header: 'Date', key: 'date', render: (r) => (r.created ? new Date(r.created).toLocaleDateString() : '—') },
              { header: 'Description', key: 'description', render: (r) => r.description || r.number || r.id },
              { header: 'Amount', key: 'amount', render: (r) => {
                // Prefer the paid amount; fall back to amount due (e.g. a
                // not-yet-paid / $0 100%-off invoice). Both are 2dp USD
                // strings from the backend.
                const amt = Number(r.amountPaidUsd) > 0 ? r.amountPaidUsd : r.amountDueUsd;
                return `$${amt} ${r.currency || 'USD'}`;
              } },
              { header: 'Status', key: 'status', render: (r) => {
                const s = (r.status || 'unknown').toLowerCase();
                return <span className={`status-badge status-${s}`}>{s}</span>;
              } },
              { header: '', key: 'links', render: (r) => (
                <span style={{ display: 'inline-flex', gap: 12 }}>
                  {r.hostedInvoiceUrl && (
                    <a href={r.hostedInvoiceUrl} target="_blank" rel="noopener noreferrer" className="btn-link">View</a>
                  )}
                  {r.invoicePdf && (
                    <a href={r.invoicePdf} target="_blank" rel="noopener noreferrer" className="btn-link">PDF</a>
                  )}
                </span>
              ) },
            ]}
            data={invoices}
            emptyState={{
              title: 'No invoices yet',
              description: 'Invoices appear here after your first credit top-up.',
            }}
          />
          {/* "Load more" pulls the next (older) Stripe page via the cursor and
              appends. Hidden once the oldest invoice is loaded (cursor null). */}
          {invoicesCursor && (
            <div style={{ display: 'flex', justifyContent: 'center', marginTop: 12 }}>
              <IonButton
                fill="clear"
                size="small"
                className="btn-white"
                disabled={invoicesLoadingMore}
                onClick={loadMoreInvoices}
              >
                {invoicesLoadingMore ? 'Loading…' : 'Load more'}
              </IonButton>
            </div>
          )}
        </div>

        {/* Add-credits modal — single instance, opened from balance hero
            or from the inline "Add credits" CTA on insufficient-balance
            addon warnings. */}
        {addCreditsOpen && (
          <AddCreditsModal
            isOpen={addCreditsOpen}
            onClose={() => setAddCreditsOpen(false)}
          />
        )}

        {/* Coupon redemption modal — fires CREDIT_CHANGED_EVENT on success,
            so the balance hero, header chip, and ledger all refresh
            automatically. */}
        {couponOpen && (
          <CouponModal
            isOpen={couponOpen}
            onClose={() => setCouponOpen(false)}
          />
        )}
      </div>
    </>
  );
};

export default BillingPage;
